import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import QuestionnaireStepper from "./components/QuestionnaireStepper";
import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import SectionPanel from "./components/SectionPanel";
import { useQuery } from "@tanstack/react-query";
import {
  getQuestionnaireData,
  getQuestionsData,
  getSectionData,
  submitAnswers,
  // triggerAIGeneration,
  // fetchAIGeneratedQuestionnaire,
} from "./utils/app.utils";
import mapAnswersToPayload, { buildSafeAnswer } from "./utils/common.utils";

function App() {
  const { applicantId } = useParams<{ applicantId: string }>();
  const parsedApplicantId = Number(applicantId);

  // UUID passed from link or elsewhere (replace accordingly)
  const [uuid, setUuid] = useState<string>("sample-uuid-from-link");
  const [completed, setCompleted] = useState(false);

  const [questionnaire, setQuestionnaire] = useState<any[]>([]);
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState(0);
  const [submitted, setSubmitted] = useState<{ [index: number]: boolean }>({
    0: false,
    1: false,
  });

  // Phase states: 'basic' (9,10), 'waiting', 'main' (ai-generated)
  const [phase, setPhase] = useState<"basic" | "waiting" | "main">("basic");

  // Load initial questionnaires (e.g., id 9 and 10)
  const { data: QuestionnaireData = [], isLoading } = useQuery({
    queryKey: ["commonSectionLabels"],
    queryFn: getQuestionnaireData,
  });

  useEffect(() => {
    if (QuestionnaireData.length) {
      // Filter for id 9 and 10 initially
      if (phase === "basic") {
        const filtered = QuestionnaireData.filter(
          (item: { id: number }) => item.id === 9 || item.id === 10
        );

        setQuestionnaire(filtered);
        setCurrentQuestionnaire(0);
      }
      // If phase main, questionnaire will be set after AI generation (below)
    }
  }, [QuestionnaireData, phase]);

  // Handle submit of questionnaire form data
  // const handleSectionSubmit = async (formData: any) => {
  //   // Include uuid in submission payload for backend creation/linking
  //   const questionnaireId = questionnaire[currentQuestionnaire].id;

  //   // await submitQuestionnaireData({
  //   //   uuid,
  //   //   questionnaireId,
  //   //   answers: formData,
  //   // });

  //   setSubmitted((prev) => ({
  //     ...prev,
  //     [currentQuestionnaire]: true,
  //   }));

  //   // Advance or trigger AI generation
  //   if (currentQuestionnaire < questionnaire.length - 1) {
  //     setCurrentQuestionnaire((prev) => prev + 1);
  //   } else if (phase === "basic") {
  //     setPhase("waiting");

  //     // Simulating AI generation delay (3s)
  //     setTimeout(() => {
  //       const aiQuestionnaire = QuestionnaireData.find(
  //         (item: { id: number }) => item.id === 7
  //       );
  //       if (aiQuestionnaire) {
  //         setQuestionnaire([aiQuestionnaire]);
  //         setCurrentQuestionnaire(0);
  //         setSubmitted({ 0: false });
  //         setPhase("main");
  //       }
  //     }, 4000);
  //   } else if (phase === "main") {
  //     setCompleted(true);
  //   }
  // };

  const fetchAllQuestionsForQuestionnaire = async (questionnaireId: number) => {
    // Get all sections of the questionnaire
    const sections = await getSectionData(questionnaireId);

    //  Fetch questions for each section
    const questionsBySection = await Promise.all(
      sections.map((section: any) => getQuestionsData(section.id))
    );

    // Flatten into a single array
    return questionsBySection.flat();
  };

  const handleSectionSubmit = async ({
    questionnaireId,
    sectionId,
    answers,
  }: {
    questionnaireId: number;
    sectionId: number;
    answers: Record<number, any>;
  }) => {
    try {
      //  SUBMIT BASIC PHASE DATA TO BACKEND

      if (phase === "basic" || phase === "main") {
        // Fetch questions metadata (needed for question_no, type mapping)
        const allQuestions = await fetchAllQuestionsForQuestionnaire(
          questionnaire[currentQuestionnaire].id
        );

        // Transform UI answers → backend sp_answers rows
        const payload = mapAnswersToPayload({
          answers,
          questions: allQuestions,
          applicantId: parsedApplicantId,
          questionnaireId,
          sectionId,
        });

        // Safety check – avoid empty inserts

        if (payload.length > 0) {
          // await submitAnswers(payload);

          for (const rawAnswer of payload) {
            const cleanAnswer = buildSafeAnswer(rawAnswer);

            await submitAnswers(cleanAnswer);
          }
        }
      }

      //  MARK CURRENT QUESTIONNAIRE AS SUBMITTED

      setSubmitted((prev) => ({
        ...prev,
        [currentQuestionnaire]: true,
      }));

      //  MOVE TO NEXT QUESTIONNAIRE OR NEXT PHASE

      if (currentQuestionnaire < questionnaire.length - 1) {
        // Move to next basic questionnaire (e.g., 9 → 10)
        setCurrentQuestionnaire((prev) => prev + 1);
        return;
      }

      // BASIC PHASE COMPLETED → TRIGGER AI PHASE

      if (phase === "basic") {
        setPhase("waiting");

        // Simulate AI generation delay (POC behavior preserved)
        setTimeout(() => {
          const aiQuestionnaire = QuestionnaireData.find(
            (item: { id: number }) => item.id === 7
          );

          if (aiQuestionnaire) {
            setQuestionnaire([aiQuestionnaire]);
            setCurrentQuestionnaire(0);
            setSubmitted({ 0: false });
            setPhase("main");
          }
        }, 4000);

        return;
      }

      //  MAIN PHASE COMPLETED

      if (phase === "main") {
        setCompleted(true);
      }
    } catch (error) {
      console.error("Error submitting section answers:", error);
      alert(
        "Ihre Antworten konnten nicht gespeichert werden. Bitte versuchen Sie es erneut."
      );
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {completed ? (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography variant="h4" color="primary" gutterBottom>
            Vielen Dank für Ihre Teilnahme!
          </Typography>
          <Typography variant="body1">
            Ihre Antworten wurden gespeichert. Bei Fragen wenden Sie sich bitte
            an unser Team.
          </Typography>
        </Box>
      ) : (
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Paper elevation={3} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
              <QuestionnaireStepper
                questionnaire={questionnaire}
                currentQuestionnaire={currentQuestionnaire}
              />
            </Paper>

            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                minHeight: 400,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {phase === "waiting" ? (
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography>
                    Generieren personalisierter AI-Fragen ...
                  </Typography>
                </Box>
              ) : questionnaire.length > 0 &&
                !submitted[currentQuestionnaire] ? (
                <SectionPanel
                  questionnaire={questionnaire[currentQuestionnaire]}
                  onSectionComplete={handleSectionSubmit}
                  isLast={
                    phase === "main" ||
                    currentQuestionnaire === questionnaire.length - 1
                  }
                />
              ) : (
                submitted[currentQuestionnaire] && (
                  <Typography sx={{ mt: 3 }}>
                    You completed this questionnaire.
                  </Typography>
                )
              )}
            </Paper>
          </Box>
        </Container>
      )}
    </Container>
  );
}

export default App;
